import type { ItemType } from '@/types/pricebook';
import { isFlatPricedService } from '@/lib/sales/service-rollup';
import { Boxes, Package, Wrench, HardHat, CreditCard, Receipt } from 'lucide-react';

export function fmt(amount: number) {
  return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(amount);
}

export interface PricebookPricingLike {
  type?: ItemType | string | null;
  price?: number | null;
  priceStrategy?: string | null;
  laborRate?: number | null;
}

export function isTimeAndMaterialsPricebookItem(item: PricebookPricingLike): boolean {
  return (
    String(item.type ?? '').toLowerCase() === 'service' &&
    String(item.priceStrategy ?? '').toUpperCase() === 'DYNAMIC'
  );
}

/**
 * A SERVICE sold for one flat price the operator typed.
 *
 * The distinction that matters: on this path the price IS the price, and the
 * service's included materials and equipment are what delivering it costs
 * rather than extras charged on top. Time & materials is the other rule — there
 * the labor and the parts are exactly what sums to the price — and a BUNDLE is
 * not a service, so neither answers true here.
 *
 * The pricebook's view of the SAME rule the document side applies — the
 * catalog spells its types in lower case and the estimate in upper, and that is
 * the only difference. Delegated rather than restated so a flat service cannot
 * be flat in the pricebook and summed on the estimate.
 */
export function isFlatPricePricebookService(item: PricebookPricingLike): boolean {
  return isFlatPricedService(item);
}

export function pricebookPrimaryPrice(item: PricebookPricingLike): number {
  const amount = isTimeAndMaterialsPricebookItem(item) ? item.laborRate : item.price;
  const numeric = Number(amount ?? 0);
  return Number.isFinite(numeric) ? numeric : 0;
}

export function pricebookPrimaryPriceSuffix(item: PricebookPricingLike): string {
  return isTimeAndMaterialsPricebookItem(item) ? '/hr' : '';
}

export function pricebookPrimaryPriceLabel(item: PricebookPricingLike): string {
  return isTimeAndMaterialsPricebookItem(item) ? 'Labor Rate' : 'Price';
}

/**
 * What prices this item, in the operator's words.
 *
 * Read off `priceStrategy` — the field that actually prices — rather than the
 * legacy `priceType`, which has only two values and so reports a bundle summed
 * from its contents as a flat price the operator never typed.
 */
export function priceStrategyLabel(item: PricebookPricingLike): string {
  const strategy = String(item.priceStrategy ?? '').toUpperCase();
  if (strategy === 'DYNAMIC') return 'Dynamic pricing';
  if (strategy === 'COMPONENT_SUM') return 'Summed from contents';
  return 'Flat price';
}

export const TYPE_BADGE_CONFIG: Record<ItemType, { icon: typeof Package; className: string }> = {
  service: {
    icon: Wrench,
    className: 'bg-blue-50 text-blue-700 dark:bg-blue-950/30 dark:text-blue-400',
  },
  material: {
    icon: Package,
    className: 'bg-gray-100 text-gray-700 dark:bg-gray-800 dark:text-gray-300',
  },
  equipment: {
    icon: HardHat,
    className: 'bg-amber-50 text-amber-700 dark:bg-amber-950/30 dark:text-amber-400',
  },
  membership: {
    icon: CreditCard,
    className: 'bg-green-50 text-green-700 dark:bg-green-950/30 dark:text-green-400',
  },
  bundle: {
    icon: Boxes,
    className: 'bg-amber-50 text-amber-700 dark:bg-amber-950/30 dark:text-amber-400',
  },
  fee: {
    icon: Receipt,
    className: 'bg-purple-50 text-purple-700 dark:bg-purple-950/30 dark:text-purple-400',
  },
};
